import styled, { keyframes } from 'styled-components'
import { tokens } from 'styling/tokens'

export const CoverageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
`

export const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const Subtitle = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  min-width: 0;
`

export const MatrixLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  flex: none;
  cursor: pointer;
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${tokens.accent.onDark};

  &:hover {
    text-decoration: underline;
  }
`

export const ToggleRow = styled.div`
  display: flex;
  gap: 0.25rem;
`

export const ToggleBtn = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? tokens.accent.base : tokens.border.hairline)};
  background: ${({ $active }) => ($active ? tokens.accent.soft : 'transparent')};
  color: ${({ $active }) => ($active ? tokens.accent.base : tokens.text.secondary)};
  font-size: ${tokens.font.micro};
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: ${tokens.radius.chip};
  cursor: pointer;
  transition: all ${tokens.motion.duration} ${tokens.motion.ease};
`

export const SiteSection = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-of-type {
    border-bottom: none;
  }
`

export const SiteHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
`

export const SiteName = styled.span`
  font-size: ${tokens.font.label};
  font-weight: 700;
  color: ${tokens.text.primary};
`

export const SiteAgg = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`

export const ShiftList = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
`

export const ShiftRow = styled.button<{ $dim?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  opacity: ${({ $dim }) => ($dim ? 0.4 : 1)};
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const ShiftHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
`

export const ActiveDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  flex: none;
  border-radius: 100%;
  background: ${tokens.accent.base};
  box-shadow: ${tokens.accent.glow};
`

export const ShiftName = styled.span`
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.text.primary};
  white-space: nowrap;
`

export const ShiftWindow = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  white-space: nowrap;
  margin-right: auto;
`

export const CritPill = styled.span`
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.05rem 0.375rem;
  border-radius: ${tokens.radius.chip};
  color: ${tokens.status.danger.fg};
  background: ${tokens.status.danger.bg};
  border: 1px solid ${tokens.status.danger.fg};
  flex: none;
`

export const Numbers = styled.span<{ $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  font-size: ${tokens.font.label};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  flex: none;
  color: ${({ $tone }) => ($tone === 'neutral' ? tokens.text.secondary : tokens.status[$tone].fg)};
`

export const Chevron = styled.span<{ $open: boolean }>`
  flex: none;
  color: ${tokens.text.muted};
  font-size: ${tokens.font.micro};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform ${tokens.motion.duration} ${tokens.motion.ease};
`

export const CoverageTrack = styled.div`
  position: relative;
  width: 100%;
  height: 0.5rem;
  border-radius: ${tokens.radius.chip};
  background: ${tokens.surface.inset};
  overflow: hidden;
`

export const CoverageFill = styled.div<{ $pct: number; $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => $pct}%;
  border-radius: ${tokens.radius.chip};
  background: ${({ $tone }) => ($tone === 'neutral' ? tokens.border.strong : tokens.status[$tone].fg)};
  opacity: 0.85;
`

export const ConfirmedTick = styled.div<{ $pct: number }>`
  position: absolute;
  top: -0.125rem;
  bottom: -0.125rem;
  left: ${({ $pct }) => $pct}%;
  width: 2px;
  background: ${tokens.text.primary};
  opacity: 0.55;
`

export const SubLine = styled.span<{ $tone?: 'danger' | 'info' }>`
  font-size: ${tokens.font.micro};
  color: ${({ $tone }) =>
    $tone === 'danger' ? tokens.status.danger.fg : $tone === 'info' ? tokens.status.info.fg : tokens.text.muted};
`

export const DeptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.375rem 0 0.125rem 0.875rem;
`

export const DeptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const DeptName = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.secondary};
  min-width: 5.5rem;
`

export const DeptTrack = styled.div`
  position: relative;
  flex: 1;
  height: 0.375rem;
  border-radius: ${tokens.radius.chip};
  background: ${tokens.surface.inset};
  overflow: hidden;
`

export const GapLine = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${tokens.font.micro};
  color: ${tokens.status.danger.fg};
  padding-left: 0.875rem;
`

export const HiddenLine = styled.button`
  background: none;
  border: none;
  padding: 0.375rem 0;
  text-align: left;
  cursor: pointer;
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};

  span {
    color: ${tokens.accent.onDark};
    font-weight: 600;
  }
`

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.625rem;
  padding-top: 0.25rem;
  border-top: 1px solid ${tokens.border.hairline};
`

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const LegendSwatch = styled.span<{ $color: string; $glow?: boolean }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: ${({ $glow }) => ($glow ? '100%' : '2px')};
  background: ${({ $color }) => $color};
  ${({ $glow }) => $glow && `box-shadow: ${tokens.accent.glow};`}
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem 0;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const EmptyCta = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.accent.onDark};

  &:hover {
    text-decoration: underline;
  }
`

const pulse = keyframes`
  0% { opacity: 0.35; }
  50% { opacity: 0.7; }
  100% { opacity: 0.35; }
`

export const SkeletonRow = styled.div`
  height: 2.25rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  animation: ${pulse} 1.4s ease-in-out infinite;
  margin-bottom: 0.5rem;
`

export const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem 0;
  font-size: ${tokens.font.label};
  color: ${tokens.text.secondary};
`
